import { useEffect, useMemo, useState } from "react";
import { Routes, Route, useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShoppingBag, Heart, Trash2, Minus, Plus, ArrowLeft, MapPin, Package, Truck, CheckCircle2, Search, Star, LogOut, Play } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, fmtINR, fmtDate } from "@/lib/api";
import { StorefrontProvider, useSF, FontLoader, cssVars, TButton, Badge, THeader, TFooter, SectionRenderer, ProductGrid, EmptyBlock, fmt } from "@/storefront/engine";

// Module-level base path for all storefront links: "" on custom domains (root-mounted),
// "/store/<slug>" on DropPartner URLs. Set once per page load by the Storefront root.
let SF_BASE = "";

function setMeta(name, content, og = false) {
  if (!content) return;
  const attr = og ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function useStoreData(slug) {
  const { data: full, isLoading, error } = useQuery({
    queryKey: ["sf-full", slug],
    queryFn: async () => (await api.get(`/public${SF_BASE}/full`)).data,
  });
  const { data: products } = useQuery({
    queryKey: ["sf-products", slug],
    queryFn: async () => (await api.get(`/public${SF_BASE}/products?limit=100`)).data,
    enabled: !!full,
  });
  return { full, products: products?.items || [], isLoading, error };
}

function Shell({ children }) {
  const { t, fonts, tpl, store } = useSF();
  const loc = useLocation();
  useEffect(() => {
    document.title = store.seo_title || store.name;
    // Canonical always points at the CURRENT host — custom domain or DropPartner fallback (§17)
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + loc.pathname);
  }, [store, loc.pathname]);
  return (
    <div className="min-h-screen" style={{ ...cssVars(t, fonts, tpl), background: t.bg, color: t.text, fontFamily: "var(--sf-font-body)" }} data-testid="storefront">
      <FontLoader fonts={fonts} />
      <THeader />
      <main className="min-h-[50vh]">{children}</main>
      <TFooter />
    </div>
  );
}

function PageHead({ title, sub }) {
  const { t } = useSF();
  return (
    <div className="mx-auto max-w-7xl px-4 pt-12 sm:px-10" data-testid="page-head">
      <h1 className="text-3xl sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{title}</h1>
      {sub && <p className="mt-2 max-w-xl text-sm" style={{ color: t.textSecondary }}>{sub}</p>}
    </div>
  );
}

function HomePage() {
  return <SectionRenderer />;
}

function ShopPage() {
  const { products, t } = useSF();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState("new");
  const [cat, setCat] = useState(params.get("category") || "");
  const [maxPrice, setMaxPrice] = useState("");
  const [onlyDeals, setOnlyDeals] = useState(false);
  const [visible, setVisible] = useState(12);
  const q = params.get("q") || "";

  const cats = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const filtered = useMemo(() => {
    let list = [...products];
    if (q) list = list.filter((p) => (p.name + p.sku + (p.brand || "")).toLowerCase().includes(q.toLowerCase()));
    if (cat) list = list.filter((p) => p.category === cat);
    if (maxPrice) list = list.filter((p) => (p.price ?? p.suggested_price) <= Number(maxPrice));
    if (onlyDeals) list = list.filter((p) => p.mrp > (p.price ?? p.suggested_price));
    if (sort === "price_low") list.sort((a, b) => (a.price ?? a.suggested_price) - (b.price ?? b.suggested_price));
    if (sort === "price_high") list.sort((a, b) => (b.price ?? b.suggested_price) - (a.price ?? a.suggested_price));
    if (sort === "discount") list.sort((a, b) => ((b.mrp - (b.price ?? b.suggested_price)) || 0) - ((a.mrp - (a.price ?? a.suggested_price)) || 0));
    return list;
  }, [products, q, cat, maxPrice, onlyDeals, sort]);

  return (
    <Shell>
      <PageHead title={q ? `Results for “${q}”` : cat || "Shop All"} sub={`${filtered.length} products`} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="shop-filters">
          <select data-testid="filter-category" value={cat} onChange={(e) => { setCat(e.target.value); setVisible(12); }}
            className="border bg-transparent px-3 py-2 text-xs outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }}>
            <option value="">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="border bg-transparent px-3 py-2 text-xs outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }}>
            <option value="new">Newest</option><option value="price_low">Price: Low → High</option>
            <option value="price_high">Price: High → Low</option><option value="discount">Biggest discount</option>
          </select>
          <input data-testid="filter-max-price" type="number" value={maxPrice} onChange={(e) => { setMaxPrice(e.target.value); setVisible(12); }}
            placeholder="Max price ₹" className="w-28 border bg-transparent px-3 py-2 text-xs outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }} />
          <label className="flex items-center gap-2 text-xs" style={{ color: t.textSecondary }}>
            <input data-testid="filter-deals" type="checkbox" checked={onlyDeals} onChange={(e) => setOnlyDeals(e.target.checked)} /> Deals only
          </label>
        </div>
        {!filtered.length ? <EmptyBlock title="No products found" sub="Try removing a filter or searching something else." testid="shop-empty" /> : (
          <>
            <ProductGrid items={filtered.slice(0, visible)} testid="shop-grid" />
            {filtered.length > visible && (
              <div className="mt-10 text-center">
                <TButton data-testid="load-more-btn" variant="secondary" onClick={() => setVisible((v) => v + 12)}>Load more ({filtered.length - visible} left)</TButton>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function CategoryPage() {
  const { cat } = useParams();
  return <ShopCategoryInner cat={decodeURIComponent(cat)} />;
}
function ShopCategoryInner({ cat }) {
  return <CategoryShell cat={cat} />;
}
function CategoryShell({ cat }) {
  const { products, pages } = useSF();
  const items = products.filter((p) => p.category === cat);
  return (
    <Shell>
      <PageHead title={cat} sub={pages.shop?.seo_description || `${items.length} products in ${cat}`} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-10">
        {!items.length ? <EmptyBlock title="Nothing here yet" sub="This category has no products right now." testid="category-empty" /> : <ProductGrid items={items} testid="category-grid" />}
      </div>
    </Shell>
  );
}

function ProductPage() {
  const { pid } = useParams();
  const { slug, products, t, addToCart, toggleWish, wishlist, store } = useSF();
  const nav = useNavigate();
  const { data } = useQuery({
    queryKey: ["sf-product", slug, pid],
    queryFn: async () => (await api.get(`/public${SF_BASE}/products/${pid}`)).data,
  });
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => {
    if (!data) return;
    document.title = data.meta_title || `${data.name} — ${store?.name || "Shop"}`;
    setMeta("description", data.meta_description || data.short_description || "");
    setMeta("keywords", data.keywords || "");
    setMeta("og:title", data.meta_title || data.name, true);
    setMeta("og:description", data.meta_description || data.short_description || "", true);
    setMeta("og:image", imgUrl((data.images || [])[0] || ""), true);
  }, [data, store]);
  if (!data) return <Shell><div className="p-20 text-center text-sm" style={{ color: t.muted }}>Loading…</div></Shell>;
  const p = data;
  const media = [
    ...(p.images || []).map((u) => ({ type: "image", url: u })),
    ...(p.videos || []).map((u) => ({ type: "video", url: u })),
  ];
  const active = media[Math.min(imgIdx, media.length - 1)];
  const price = p.price ?? p.suggested_price;
  const off = p.mrp && p.mrp > price ? Math.round(((p.mrp - price) / p.mrp) * 100) : 0;
  const stock = p.available ?? (p.stock ?? 0);
  const wished = wishlist.includes(p.id || p._id);
  const related = (data.related || products.filter((x) => x.category === p.category && (x.id || x._id) !== (p.id || p._id))).slice(0, 4);

  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-10" data-testid="product-page">
        <Link to={`${SF_BASE}/shop`} className="mb-6 inline-flex items-center gap-1 text-xs uppercase tracking-widest" style={{ color: t.muted }}><ArrowLeft size={12} /> Back to shop</Link>
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="relative overflow-hidden border" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
              {active?.type === "video" ? (
                <video key={active.url} src={imgUrl(active.url)} controls playsInline preload="metadata" data-testid="product-video"
                  className="aspect-square w-full object-cover" style={{ background: "#000" }} />
              ) : (
                <img src={imgUrl(active?.url)} alt={p.name} className="aspect-square w-full object-cover" data-testid="product-image" />
              )}
              {off > 0 && <Badge className="absolute left-4 top-4">-{off}%</Badge>}
            </div>
            {media.length > 1 && (
              <div className="mt-3 flex gap-2">
                {media.map((m, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} data-testid={`thumb-${i}`}
                    className="relative h-16 w-16 overflow-hidden border" style={{ borderColor: i === imgIdx ? t.primary : t.border, borderRadius: "var(--sf-radius-card)" }}>
                    {m.type === "video" ? (
                      <>
                        <video src={imgUrl(m.url)} muted preload="metadata" className="h-full w-full object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Play size={16} color="#fff" fill="#fff" /></span>
                      </>
                    ) : (
                      <img src={imgUrl(m.url)} alt="" className="h-full w-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: t.muted }}>{p.category || "Product"}</p>
            <h1 className="mt-2 text-3xl sm:text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }} data-testid="product-name">{p.name}</h1>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-2xl font-bold" style={{ color: t.primary }} data-testid="product-price">{fmt(price)}</span>
              {p.mrp > price && <span className="text-sm line-through" style={{ color: t.muted }}>{fmt(p.mrp)}</span>}
              {off > 0 && <Badge>Save {off}%</Badge>}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: t.muted }}>Inclusive of all taxes (GST)</p>
            <p className="mt-2 text-xs font-semibold" style={{ color: stock > 0 ? t.primary : "#e11d48" }} data-testid="stock-status">
              {stock > 0 ? `In stock · ${stock} available` : "Out of stock"}
            </p>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: t.textSecondary }} data-testid="product-description">{p.description || p.short_description || "Quality product from our catalog."}</p>
            {p.specifications && typeof p.specifications === "object" && Object.keys(p.specifications).length > 0 && (
              <div className="mt-5 border p-4 text-xs" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }}>
                {Object.entries(p.specifications).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1"><span style={{ color: t.muted }}>{k}</span><span style={{ color: t.text }}>{v}</span></div>
                ))}
              </div>
            )}
            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center border" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-btn)" }}>
                <button data-testid="qty-minus" className="px-3 py-2" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={13} /></button>
                <span className="w-8 text-center text-sm font-bold" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" className="px-3 py-2" onClick={() => setQty(Math.min(stock || 99, qty + 1))}><Plus size={13} /></button>
              </div>
              <TButton data-testid="add-to-cart-btn" disabled={stock <= 0} onClick={() => { addToCart(p.id || p._id, qty); toast.success("Added to cart"); }} className="flex-1">
                <ShoppingBag size={14} /> Add to Cart
              </TButton>
              <button data-testid="wishlist-btn" onClick={() => toggleWish(p.id || p._id)} className="border p-3" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-btn)" }}>
                <Heart size={16} fill={wished ? t.primary : "none"} color={wished ? t.primary : t.text} />
              </button>
            </div>
            <TButton data-testid="buy-now-btn" variant="secondary" disabled={stock <= 0} className="mt-3 w-full"
              onClick={() => { addToCart(p.id || p._id, qty); nav(`${SF_BASE}/checkout`); }}>Buy Now</TButton>
            <div className="mt-6 flex flex-wrap gap-4 text-[11px]" style={{ color: t.muted }}>
              <span className="flex items-center gap-1.5"><Truck size={13} /> 2–5 day delivery</span>
              <span className="flex items-center gap-1.5"><Package size={13} /> COD available</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={13} /> Easy returns</span>
            </div>
          </div>
        </div>
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>You may also like</h2>
            <div className="mt-6"><ProductGrid items={related} testid="related-grid" /></div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function SearchPage() { return <ShopPage />; }

function CartPage() {
  const { cart, setCart, products, t, slug } = useSF();
  const rows = cart.map((i) => ({ ...i, product: products.find((p) => (p.id || p._id) === i.product_id) })).filter((r) => r.product);
  const subtotal = rows.reduce((s, r) => s + (r.product.price ?? r.product.suggested_price) * r.qty, 0);
  return (
    <Shell>
      <PageHead title="Your Cart" sub={`${rows.length} item(s)`} />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-10">
        {!rows.length ? <EmptyBlock title="Your cart is empty" sub="Browse the shop and add something you love." testid="cart-empty" /> : (
          <>
            <div className="space-y-3" data-testid="cart-items">
              {rows.map((r) => (
                <div key={r.product_id} className="flex items-center gap-4 border p-4" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
                  <img src={imgUrl((r.product.images || [])[0])} alt="" className="h-16 w-16 object-cover" style={{ borderRadius: "var(--sf-radius-card)" }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold" style={{ color: t.text }}>{r.product.name}</p>
                    <p className="text-xs" style={{ color: t.muted }}>{fmt(r.product.price ?? r.product.suggested_price)} each</p>
                  </div>
                  <div className="flex items-center border" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-btn)" }}>
                    <button data-testid={`cart-minus-${r.product.sku}`} className="px-2 py-1" onClick={() => setCart(cart.map((i) => i.product_id === r.product_id ? { ...i, qty: Math.max(1, i.qty - 1) } : i))}><Minus size={11} /></button>
                    <span className="w-7 text-center text-xs font-bold">{r.qty}</span>
                    <button data-testid={`cart-plus-${r.product.sku}`} className="px-2 py-1" onClick={() => setCart(cart.map((i) => i.product_id === r.product_id ? { ...i, qty: i.qty + 1 } : i))}><Plus size={11} /></button>
                  </div>
                  <p className="w-20 text-right text-sm font-bold" style={{ color: t.text }}>{fmt((r.product.price ?? r.product.suggested_price) * r.qty)}</p>
                  <button data-testid={`cart-remove-${r.product.sku}`} onClick={() => setCart(cart.filter((i) => i.product_id !== r.product_id))} style={{ color: t.muted }}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="mt-6 border p-5" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }} data-testid="cart-summary">
              <div className="flex justify-between text-sm"><span style={{ color: t.textSecondary }}>Subtotal (incl. GST)</span><b style={{ color: t.text }}>{fmt(subtotal)}</b></div>
              <div className="mt-1 flex justify-between text-sm"><span style={{ color: t.textSecondary }}>Shipping</span><span style={{ color: t.textSecondary }}>Calculated at checkout</span></div>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={`${SF_BASE}/shop`}><TButton variant="secondary" data-testid="continue-shopping-btn">Continue Shopping</TButton></Link>
                <Link to={`${SF_BASE}/checkout`} className="flex-1"><TButton data-testid="proceed-checkout-btn" className="w-full">Proceed to Checkout</TButton></Link>
              </div>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

function CheckoutPage() {
  const { cart, setCart, products, t, slug, store, custToken } = useSF();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" });
  const [pay, setPay] = useState("cod");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!custToken) return;
    api.get(`/public${SF_BASE}/account/me`, { headers: { Authorization: `Bearer ${custToken}` } })
      .then(({ data }) => {
        const c = data.customer;
        const def = (c.addresses || []).find((a) => a.is_default) || (c.addresses || [])[0] || {};
        setForm((f) => ({ ...f, name: f.name || def.name || c.name, mobile: f.mobile || def.mobile || c.mobile,
          email: f.email || c.email, address: f.address || def.address || "", city: f.city || def.city || "",
          state: f.state || def.state || "", pincode: f.pincode || def.pincode || "" }));
      })
      .catch(() => {});
  }, [custToken, slug]);
  const rows = cart.map((i) => ({ ...i, product: products.find((p) => (p.id || p._id) === i.product_id) })).filter((r) => r.product);
  const subtotal = rows.reduce((s, r) => s + (r.product.price ?? r.product.suggested_price) * r.qty, 0);
  const [quote, setQuote] = useState(null);
  const [shipErr, setShipErr] = useState("");
  useEffect(() => {
    if (!/^\d{6}$/.test(form.pincode || "") || !rows.length) { setQuote(null); return; }
    let live = true;
    setShipErr("");
    api.post(`/public${SF_BASE}/shipping-quote`, { items: rows.map((r) => ({ product_id: r.product_id, qty: r.qty })), pincode: form.pincode })
      .then(({ data }) => { if (live) setQuote(data); })
      .catch((e) => { if (live) { setQuote(null); setShipErr(e?.response?.data?.detail || "Shipping unavailable for this PIN code"); } });
    return () => { live = false; };
  }, [form.pincode, JSON.stringify(rows.map((r) => [r.product_id, r.qty]))]); // eslint-disable-line
  useEffect(() => { if (!store.payment_online && pay !== "cod") setPay("cod"); }, [store.payment_online]); // eslint-disable-line
  const shipping = quote?.shipping_cost ?? null, codFee = pay === "cod" ? 25 : 0;
  const total = subtotal + (shipping ?? 0) + codFee;

  const placeOrder = async () => {
    if (!form.name || !form.mobile || !form.address || !form.pincode) { toast.error("Fill in name, mobile, address and PIN code"); return; }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (!quote) { toast.error(shipErr || "Enter your 6-digit PIN code to calculate exact courier charge"); return; }
    setBusy(true);
    try {
      const headers = custToken ? { Authorization: `Bearer ${custToken}` } : {};
      const { data } = await api.post(`/public${SF_BASE}/orders`, {
        customer: form, payment_method: pay,
        items: rows.map((r) => ({ product_id: r.product_id, qty: r.qty })),
      }, { headers });
      setCart([]);
      nav(`${SF_BASE}/confirmed/${data.order_no}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order — please try again");
    } finally { setBusy(false); }
  };

  if (!custToken) return (
    <Shell><div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="checkout-signin-required">
      <p className="text-xl font-bold" style={{ color: t.text }}>Sign in to place your order</p>
      <p className="mt-2 text-sm" style={{ color: t.muted }}>Create your free account or sign in — your orders, tracking and addresses are saved to your account.</p>
      <Link to={`${SF_BASE}/account`}><TButton data-testid="checkout-signin-btn" className="mt-6">Sign in / Create account</TButton></Link>
    </div></Shell>
  );
  if (!rows.length) return <Shell><div className="py-20"><EmptyBlock title="Nothing to checkout" sub="Your cart is empty." testid="checkout-empty" /></div></Shell>;
  const field = (key, label, type = "text") => (
    <div>
      <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>{label}</p>
      <input data-testid={`checkout-${key}`} type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full border bg-transparent px-3 py-2.5 text-sm outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }} />
    </div>
  );

  return (
    <Shell>
      <PageHead title="Checkout" sub={`Ordering from ${store.name}`} />
      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-8 sm:px-10 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3" data-testid="checkout-form">
          {field("name", "Full name *")}
          <div className="grid grid-cols-2 gap-4">{field("mobile", "Mobile *")}{field("email", "Email", "email")}</div>
          {field("address", "Address *")}
          <div className="grid grid-cols-3 gap-4">{field("city", "City")}{field("state", "State")}{field("pincode", "PIN *")}</div>
          <div>
            <p className="mb-2 text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>Payment</p>
            <div className="flex gap-3">
              {[["cod", "Cash on Delivery"], ...(store.payment_online ? [["online", "Pay Online (UPI/Cards)"]] : [])].map(([v, l]) => (
                <button key={v} data-testid={`pay-${v}`} onClick={() => setPay(v)}
                  className="flex-1 border px-4 py-3 text-xs font-semibold transition-colors"
                  style={{ borderColor: pay === v ? t.primary : t.border, color: t.text, borderRadius: "var(--sf-radius-card)", background: pay === v ? t.badgeBg : "transparent" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="border p-5" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }} data-testid="checkout-summary">
            <p className="text-sm font-bold" style={{ color: t.text }}>Order Summary</p>
            <div className="mt-3 space-y-2">
              {rows.map((r) => (
                <div key={r.product_id} className="flex justify-between text-xs" style={{ color: t.textSecondary }}>
                  <span className="truncate pr-2">{r.product.name} × {r.qty}</span><span>{fmt((r.product.price ?? r.product.suggested_price) * r.qty)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 border-t pt-3 text-xs" style={{ borderColor: t.border }}>
              <div className="flex justify-between"><span style={{ color: t.textSecondary }}>Subtotal (incl. GST)</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span style={{ color: t.textSecondary }}>Shipping{quote?.chargeable_kg ? ` (${quote.chargeable_kg} kg${quote.service ? ` · ${quote.service}` : ""})` : ""}</span><span>{shipping == null ? "Enter PIN code" : fmt(shipping)}</span></div>
              {shipErr && <p className="text-[11px] font-medium text-rose-500" data-testid="shipping-error">{shipErr}</p>}
              {!quote && !shipErr && /^\d{1,5}$/.test(form.pincode || "") && (
                <p className="text-[11px]" style={{ color: t.textSecondary }} data-testid="shipping-hint">Enter your full 6-digit PIN code to see the exact courier charge</p>
              )}
              {codFee > 0 && <div className="flex justify-between"><span style={{ color: t.textSecondary }}>COD fee</span><span>{fmt(codFee)}</span></div>}
              <div className="flex justify-between pt-2 text-sm font-bold" style={{ color: t.text }}><span>Total</span><span data-testid="checkout-total">{fmt(total)}</span></div>
            </div>
            <TButton data-testid="place-order-btn" onClick={placeOrder} disabled={busy} className="mt-5 w-full">
              {busy ? "Placing order…" : pay === "cod" ? "Place Order (COD)" : "Pay & Place Order"}
            </TButton>
            <p className="mt-3 text-center text-[10px]" style={{ color: t.muted }}>Secure checkout · Prices include GST</p>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function ConfirmedPage() {
  const { orderNo } = useParams();
  const { t, slug } = useSF();
  return (
    <Shell>
      <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="order-confirmed">
        <CheckCircle2 size={48} className="mx-auto" style={{ color: t.primary }} />
        <h1 className="mt-6 text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>Order Confirmed</h1>
        <p className="mt-3 text-sm" style={{ color: t.textSecondary }}>Thank you! Your order has been placed successfully.</p>
        <p className="mt-4 inline-block border px-5 py-2 font-mono text-sm font-bold" style={{ borderColor: t.border, color: t.text }} data-testid="order-number">{orderNo}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to={`${SF_BASE}/track`}><TButton data-testid="track-order-btn">Track Order</TButton></Link>
          <Link to={`${SF_BASE}/shop`}><TButton variant="secondary" data-testid="continue-shopping-2">Continue Shopping</TButton></Link>
        </div>
      </div>
    </Shell>
  );
}

const STEPS = ["placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"];

function TrackPage() {
  const { t, slug } = useSF();
  const [orderNo, setOrderNo] = useState("");
  const [mobile, setMobile] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const track = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.get(`/public${SF_BASE}/track`, { params: { order_no: orderNo.trim(), mobile: mobile.trim() } });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Order not found — check your order number and mobile.");
    } finally { setBusy(false); }
  };
  const status = (result?.status || "placed").toLowerCase();
  const activeIdx = STEPS.indexOf(status === "cancelled" || status === "rto" ? "shipped" : status);
  return (
    <Shell>
      <PageHead title="Track My Order" sub="Enter your order number and the mobile number used at checkout." />
      <div className="mx-auto max-w-xl px-4 py-10" data-testid="track-page">
        <div className="space-y-4 border p-6" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
          <input data-testid="track-order-input" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="Order number (e.g. ORD-100001)"
            className="w-full border bg-transparent px-4 py-2.5 text-sm outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }} />
          <input data-testid="track-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number"
            className="w-full border bg-transparent px-4 py-2.5 text-sm outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }} />
          <TButton data-testid="track-submit-btn" onClick={track} disabled={busy || !orderNo || !mobile} className="w-full">{busy ? "Checking…" : "Track Order"}</TButton>
          {err && <p className="text-xs" style={{ color: "#e11d48" }} data-testid="track-error">{err}</p>}
        </div>
        {result && (
          <div className="mt-8 border p-6" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }} data-testid="track-result">
            <div className="flex items-center justify-between">
              <p className="font-mono text-sm font-bold" style={{ color: t.text }}>{result.order_no}</p>
              <Badge>{result.status}</Badge>
            </div>
            {result.awb && <p className="mt-1 text-[11px]" style={{ color: t.muted }}>AWB: {result.awb}{result.courier ? ` · ${result.courier}` : ""}</p>}
            <div className="mt-6 space-y-0">
              {STEPS.map((s, i) => {
                const done = i <= activeIdx;
                return (
                  <div key={s} className="flex gap-3" data-testid={`track-step-${s}`}>
                    <div className="flex flex-col items-center">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 text-[9px]"
                        style={{ borderColor: done ? t.primary : t.border, background: done ? t.primary : "transparent", color: done ? t.buttonText : t.muted }}>
                        {done ? "✓" : i + 1}
                      </span>
                      {i < STEPS.length - 1 && <span className="h-7 w-0.5" style={{ background: done && i < activeIdx ? t.primary : t.border }} />}
                    </div>
                    <p className="pt-0.5 text-xs font-semibold capitalize" style={{ color: done ? t.text : t.muted }}>{s.replace(/_/g, " ")}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function ContentPage({ pageKey, fallbackTitle }) {
  const { pages, store, t, slug } = useSF();
  const page = pages[pageKey] || {};
  const hidden = page.is_visible === false;
  useEffect(() => { if (page.seo_title) document.title = page.seo_title; }, [page]);
  if (hidden) return <NotFoundPage />;
  return (
    <Shell>
      <PageHead title={page.title || fallbackTitle} sub={page.seo_description} />
      <div className="mx-auto max-w-3xl px-4 py-10" data-testid={`page-${pageKey}`}>
        {page.content ? (
          <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: t.textSecondary }}>{page.content}</div>
        ) : (
          <EmptyBlock title="Content coming soon" sub={`${store.name} is updating this page.`} testid={`page-${pageKey}-empty`} />
        )}
        {pageKey === "contact" && (
          <div className="mt-8 space-y-2 border p-6 text-sm" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }} data-testid="contact-details">
            {store.contact?.email && <p style={{ color: t.text }}>Email: <a href={`mailto:${store.contact.email}`} className="underline">{store.contact.email}</a></p>}
            {store.contact?.mobile && <p style={{ color: t.text }}>Phone: {store.contact.mobile}</p>}
            {store.contact?.address && <p style={{ color: t.text }}>Address: {store.contact.address}</p>}
            {store.contact?.email && (
              <a data-testid="contact-mailto" href={`mailto:${store.contact.email}?subject=Enquiry%20for%20${encodeURIComponent(store.name)}`} className="inline-block pt-2">
                <TButton>Write to us</TButton>
              </a>
            )}
          </div>
        )}
        {pageKey === "payment-info" && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="payment-methods">
            {["Cash on Delivery (COD)", "UPI / Cards / Netbanking (Razorpay)"].map((m) => (
              <div key={m} className="border p-4 text-xs font-semibold" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", color: t.text }}>{m}</div>
            ))}
          </div>
        )}
        {pageKey === "support" && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="support-links">
            {[["Track your order", "track"], ["FAQ", "faq"], ["Shipping info", "shipping"], ["Returns & refunds", "returns"]].map(([l, h]) => (
              <Link key={h} to={`${SF_BASE}/${h}`} className="border p-4 text-xs font-semibold transition-opacity hover:opacity-70"
                style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", color: t.text }}>{l} →</Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function FaqPage() {
  const { faqs, pages, t } = useSF();
  const [openIdx, setOpenIdx] = useState(null);
  const page = pages.faq || {};
  if (page.is_visible === false) return <NotFoundPage />;
  const byCat = faqs.reduce((m, f) => { (m[f.category] = m[f.category] || []).push(f); return m; }, {});
  return (
    <Shell>
      <PageHead title={page.title || "Frequently Asked Questions"} sub={page.seo_description} />
      <div className="mx-auto max-w-3xl px-4 py-10" data-testid="page-faq">
        {!faqs.length && !page.content ? <EmptyBlock title="FAQs coming soon" sub="We're putting together answers to common questions." testid="faq-empty" /> : null}
        {page.content && <p className="mb-8 text-sm leading-relaxed whitespace-pre-line" style={{ color: t.textSecondary }}>{page.content}</p>}
        {Object.entries(byCat).map(([cat, items]) => (
          <div key={cat} className="mb-8">
            <h2 className="mb-3 text-lg" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{cat}</h2>
            <div className="space-y-2">
              {items.map((f, i) => (
                <div key={f.id} className="border" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }} data-testid={`faq-item-${f.id}`}>
                  <button className="flex w-full items-center justify-between px-5 py-3.5 text-left text-sm font-semibold"
                    style={{ color: t.text }} onClick={() => setOpenIdx(openIdx === f.id ? null : f.id)} data-testid={`faq-q-${f.id}`}>
                    {f.question}
                    <span style={{ color: t.muted }}>{openIdx === f.id ? "−" : "+"}</span>
                  </button>
                  {openIdx === f.id && <p className="px-5 pb-4 text-sm leading-relaxed" style={{ color: t.textSecondary }} data-testid={`faq-a-${f.id}`}>{f.answer}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function BlogPage() {
  const { slug, t, pages } = useSF();
  const { data } = useQuery({ queryKey: ["sf-blog", slug], queryFn: async () => (await api.get(`/public${SF_BASE}/blog`)).data });
  if (pages.blog?.is_visible === false) return <NotFoundPage />;
  const items = data?.items || [];
  return (
    <Shell>
      <PageHead title="Journal" sub="Stories, guides and updates" />
      <div className="mx-auto max-w-6xl px-4 py-10" data-testid="page-blog">
        {!items.length ? <EmptyBlock title="No posts yet" sub="New stories are on the way." testid="blog-empty" /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <Link key={p.id} to={`${SF_BASE}/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className="group border" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
                {p.cover_image && <img src={imgUrl(p.cover_image)} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover" style={{ borderRadius: "var(--sf-radius-card) var(--sf-radius-card) 0 0" }} />}
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>{p.category} · {fmtDate(p.published_at || p.created_at)}</p>
                  <p className="mt-2 text-lg leading-snug" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.title}</p>
                  <p className="mt-2 text-xs" style={{ color: t.secondary }}>Read →</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function BlogPostPage() {
  const { slug, postSlug } = useParams();
  const { t } = useSF();
  const { data: post, error } = useQuery({
    queryKey: ["sf-post", slug, postSlug],
    queryFn: async () => (await api.get(`/public${SF_BASE}/blog/${postSlug}`)).data,
  });
  useEffect(() => { if (post?.seo_title) document.title = post.seo_title; else if (post) document.title = post.title; }, [post]);
  if (error) return <NotFoundPage />;
  if (!post) return null;
  return (
    <Shell>
      <article className="mx-auto max-w-3xl px-4 py-12" data-testid="blog-post">
        <Link to={`${SF_BASE}/blog`} className="text-xs uppercase tracking-widest" style={{ color: t.muted }}><ArrowLeft size={11} className="mr-1 inline" />All posts</Link>
        <h1 className="mt-4 text-3xl sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{post.title}</h1>
        <p className="mt-3 text-xs" style={{ color: t.muted }}>{post.author} · {fmtDate(post.published_at || post.created_at)}</p>
        {post.cover_image && <img src={imgUrl(post.cover_image)} alt="" className="mt-6 w-full object-cover" style={{ borderRadius: "var(--sf-radius-card)" }} />}
        <div className="mt-8 text-sm leading-loose whitespace-pre-line" style={{ color: t.textSecondary }} data-testid="blog-content">{post.content}</div>
      </article>
    </Shell>
  );
}

function WishlistPage() {
  const { wishlist, products, toggleWish, t, slug } = useSF();
  const items = products.filter((p) => wishlist.includes(p.id || p._id));
  return (
    <Shell>
      <PageHead title="My Wishlist" sub={`${items.length} saved item(s)`} />
      <div className="mx-auto max-w-6xl px-4 py-10" data-testid="page-wishlist">
        {!items.length ? <EmptyBlock title="Wishlist is empty" sub="Tap the heart on any product to save it here." testid="wishlist-empty" /> : (
          <>
            <ProductGrid items={items} testid="wishlist-grid" />
            <div className="mt-6 text-center">
              <TButton variant="secondary" data-testid="clear-wishlist" onClick={() => items.forEach((p) => toggleWish(p.id || p._id))}>Clear wishlist</TButton>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

function AccountPage() {
  const { t, slug, custToken, signInCust, signOutCust } = useSF();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", mobile: "", email: "", password: "", identifier: "" });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("orders");
  const { data: me, isLoading: meLoading, error: meError, refetch: refetchMe } = useQuery({
    queryKey: ["sf-me", slug, custToken],
    queryFn: async () => (await api.get(`/public${SF_BASE}/account/me`, { headers: { Authorization: `Bearer ${custToken}` } })).data,
    enabled: !!custToken,
    retry: false,
  });
  const customer = me?.customer;

  useEffect(() => { if (custToken && meError) signOutCust(); }, [custToken, meError]);

  const submitAuth = async () => {
    setBusy(true);
    try {
      const url = mode === "login" ? "auth/login" : "auth/register";
      const payload = mode === "login"
        ? { identifier: form.identifier, password: form.password }
        : { name: form.name, mobile: form.mobile, email: form.email, password: form.password };
      const { data } = await api.post(`/public${SF_BASE}/${url}`, payload);
      signInCust(data.token);
      toast.success(mode === "login" ? "Welcome back!" : "Account created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign in — please try again");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full border bg-transparent px-3 py-2.5 text-sm outline-none";
  const inputStyle = { borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" };

  if (custToken && meLoading) return <Shell><div className="p-20 text-center text-sm" style={{ color: t.muted }}>Loading your account…</div></Shell>;

  if (!customer) return (
    <Shell>
      <PageHead title={mode === "login" ? "Sign In" : "Create Account"} sub="Track orders, save addresses and check out faster." />
      <div className="mx-auto max-w-md px-4 py-10" data-testid="account-auth">
        <div className="mb-5 grid grid-cols-2 gap-1 border p-1 text-center text-xs font-bold uppercase tracking-widest" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-btn)" }}>
          {[["login", "Sign In"], ["register", "Register"]].map(([m, l]) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)} className="py-2.5 transition-colors"
              style={{ background: mode === m ? t.buttonBg : "transparent", color: mode === m ? t.buttonText : t.textSecondary, borderRadius: "var(--sf-radius-btn)" }}>{l}</button>
          ))}
        </div>
        <div className="space-y-3">
          {mode === "login" ? (
            <>
              <input data-testid="login-identifier" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="Email or mobile" className={inputCls} style={inputStyle} />
              <input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className={inputCls} style={inputStyle} />
            </>
          ) : (
            <>
              <input data-testid="reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} style={inputStyle} />
              <input data-testid="reg-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile (10 digits)" className={inputCls} style={inputStyle} />
              <input data-testid="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} style={inputStyle} />
              <input data-testid="reg-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6 chars)" className={inputCls} style={inputStyle} />
            </>
          )}
          <TButton data-testid="auth-submit-btn" onClick={submitAuth} disabled={busy} className="w-full">
            {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </TButton>
        </div>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <PageHead title={`Hi, ${customer.name.split(" ")[0]}`} sub={customer.email} />
      <div className="mx-auto max-w-4xl px-4 py-10" data-testid="page-account">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {[["orders", "My Orders"], ["addresses", "Addresses"], ["profile", "Profile"]].map(([k, l]) => (
            <button key={k} data-testid={`account-tab-${k}`} onClick={() => setTab(k)}
              className="border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ borderColor: tab === k ? t.primary : t.border, color: tab === k ? t.primary : t.textSecondary, borderRadius: "var(--sf-radius-btn)" }}>{l}</button>
          ))}
          <Link to={`${SF_BASE}/wishlist`} data-testid="account-tab-wishlist"
            className="border px-4 py-2 text-xs font-bold uppercase tracking-widest"
            style={{ borderColor: t.border, color: t.textSecondary, borderRadius: "var(--sf-radius-btn)" }}>Wishlist</Link>
          <button data-testid="account-signout-btn" onClick={() => { signOutCust(); toast.success("Signed out"); }}
            className="ml-auto flex items-center gap-1 text-xs uppercase tracking-widest" style={{ color: t.muted }}><LogOut size={12} /> Sign out</button>
        </div>
        {tab === "orders" && <AccountOrders slug={slug} token={custToken} t={t} />}
        {tab === "addresses" && <AccountAddresses slug={slug} token={custToken} t={t} customer={customer} refresh={refetchMe} />}
        {tab === "profile" && <AccountProfile slug={slug} token={custToken} t={t} customer={customer} refresh={refetchMe} />}
      </div>
    </Shell>
  );
}

function AccountOrders({ slug, token, t }) {
  const { data } = useQuery({
    queryKey: ["sf-my-orders", slug, token],
    queryFn: async () => (await api.get(`/public${SF_BASE}/account/orders`, { headers: { Authorization: `Bearer ${token}` } })).data,
  });
  const items = data?.items || [];
  if (!items.length) return <EmptyBlock title="No orders yet" sub="Your orders will appear here once you shop." testid="account-orders-empty" />;
  return (
    <div className="space-y-3" data-testid="account-orders-list">
      {items.map((o) => (
        <div key={o.id} className="border p-5" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }} data-testid={`account-order-${o.order_no}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-mono text-sm font-bold" style={{ color: t.text }}>{o.order_no}</p>
            <Badge>{o.status}</Badge>
          </div>
          <p className="mt-1 text-[11px]" style={{ color: t.muted }}>{fmtDate(o.created_at)} · {(o.payment_method || "").toUpperCase()} · {fmt(o.total)}</p>
          <div className="mt-3 space-y-1">
            {o.items.map((i, idx) => (
              <p key={idx} className="text-xs" style={{ color: t.textSecondary }}>{i.name} × {i.qty} — {fmt(i.price * i.qty)}</p>
            ))}
          </div>
          {o.awb && <p className="mt-2 text-[11px]" style={{ color: t.muted }}>AWB: {o.awb}</p>}
          <Link to={`${SF_BASE}/track`} className="mt-3 inline-block text-xs font-semibold underline" style={{ color: t.primary }} data-testid={`track-link-${o.order_no}`}>Track order</Link>
        </div>
      ))}
    </div>
  );
}

function AccountAddresses({ slug, token, t, customer, refresh }) {
  const blank = { label: "Home", name: customer.name, mobile: customer.mobile, address: "", city: "", state: "", pincode: "" };
  const [form, setForm] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const inputCls = "w-full border bg-transparent px-3 py-2 text-sm outline-none";
  const inputStyle = { borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" };
  const save = async () => {
    try {
      await api.post(`/public${SF_BASE}/account/addresses`, form, { headers });
      toast.success("Address saved");
      setForm(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
  };
  const del = async (id) => {
    try { await api.delete(`/public${SF_BASE}/account/addresses/${id}`, { headers }); refresh(); } catch { toast.error("Could not delete"); }
  };
  const mkDefault = async (id) => {
    try { await api.put(`/public${SF_BASE}/account/addresses/${id}`, { is_default: true }, { headers }); refresh(); } catch { toast.error("Could not update"); }
  };
  const addrs = customer.addresses || [];
  return (
    <div data-testid="account-addresses">
      {!addrs.length && !form && <EmptyBlock title="No saved addresses" sub="Add an address to check out faster." testid="addresses-empty" />}
      <div className="grid gap-3 sm:grid-cols-2">
        {addrs.map((a) => (
          <div key={a.id} className="border p-4 text-xs" style={{ borderColor: a.is_default ? t.primary : t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }} data-testid={`address-${a.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-bold uppercase tracking-widest" style={{ color: t.text }}>{a.label}</p>
              {a.is_default && <Badge>Default</Badge>}
            </div>
            <p className="mt-2" style={{ color: t.text }}>{a.name} · {a.mobile}</p>
            <p className="mt-1 leading-relaxed" style={{ color: t.textSecondary }}>{a.address}, {a.city}, {a.state} — {a.pincode}</p>
            <div className="mt-3 flex gap-3">
              {!a.is_default && <button data-testid={`address-default-${a.id}`} onClick={() => mkDefault(a.id)} className="font-semibold underline" style={{ color: t.primary }}>Set default</button>}
              <button data-testid={`address-del-${a.id}`} onClick={() => del(a.id)} className="text-rose-500 underline">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {form ? (
        <div className="mt-5 space-y-3 border p-5" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }} data-testid="address-form">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="addr-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (Home/Work)" className={inputCls} style={inputStyle} />
            <input data-testid="addr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className={inputCls} style={inputStyle} />
          </div>
          <input data-testid="addr-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile *" className={inputCls} style={inputStyle} />
          <input data-testid="addr-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address (house, street, area) *" className={inputCls} style={inputStyle} />
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="addr-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City *" className={inputCls} style={inputStyle} />
            <input data-testid="addr-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State *" className={inputCls} style={inputStyle} />
            <input data-testid="addr-pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="PIN *" className={inputCls} style={inputStyle} />
          </div>
          <div className="flex gap-2">
            <TButton data-testid="addr-save-btn" onClick={save}>Save address</TButton>
            <TButton variant="secondary" data-testid="addr-cancel-btn" onClick={() => setForm(null)}>Cancel</TButton>
          </div>
        </div>
      ) : (
        <div className="mt-5"><TButton variant="secondary" data-testid="add-address-btn" onClick={() => setForm(blank)}>Add new address</TButton></div>
      )}
    </div>
  );
}

function AccountProfile({ slug, token, t, customer, refresh }) {
  const [form, setForm] = useState({ name: customer.name, email: customer.email });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full border bg-transparent px-3 py-2.5 text-sm outline-none";
  const inputStyle = { borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/public${SF_BASE}/account/profile`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile updated");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md space-y-3" data-testid="account-profile">
      <div>
        <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>Full name</p>
        <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${inputCls}`} style={inputStyle} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>Email</p>
        <input data-testid="profile-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`mt-1 ${inputCls}`} style={inputStyle} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>Mobile (login id — cannot change)</p>
        <input data-testid="profile-mobile" value={customer.mobile} disabled className={`mt-1 ${inputCls} opacity-60`} style={inputStyle} />
      </div>
      <TButton data-testid="profile-save-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save profile"}</TButton>
    </div>
  );
}

function NotFoundPage() {
  return (
    <Shell>
      <div className="mx-auto max-w-md px-4 py-28 text-center" data-testid="page-404">
        <NotFoundInner />
      </div>
    </Shell>
  );
}
function NotFoundInner() {
  const { t, slug } = useSF();
  return (
    <>
      <p className="text-7xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.primary }}>404</p>
      <p className="mt-3 text-lg" style={{ color: t.text }}>This page wandered off</p>
      <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>The link may be old, or the page was moved.</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to={`${SF_BASE}`}><TButton data-testid="404-home">Back Home</TButton></Link>
        <Link to={`${SF_BASE}/shop`}><TButton variant="secondary" data-testid="404-shop">Continue Shopping</TButton></Link>
      </div>
    </>
  );
}

export default function Storefront() {
  const params = useParams();
  // Custom-domain mode: HostGate sets window.__CUSTOM_STORE_SLUG__ and mounts us at root —
  // same store, same components, browser keeps the agent's domain (no redirect).
  const slug = window.__CUSTOM_STORE_SLUG__ || params.slug;
  SF_BASE = window.__CUSTOM_STORE_SLUG__ ? "" : `/store/${slug}`;
  const { full, products, isLoading, error } = useStoreData(slug);

  if (isLoading) return <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] text-sm text-slate-500">Loading store…</div>;
  if (error || !full) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 text-center">
      <h1 className="text-xl font-bold text-slate-700">Store not found</h1>
      <p className="mt-1 text-sm text-slate-500">This store may be unpublished or the link is incorrect.</p>
    </div>
  );

  return (
    <StorefrontProvider payload={full} products={products} slug={slug}>
      <Routes>
        <Route index element={<Shell><HomePage /></Shell>} />
        <Route path="shop" element={<ShopPage />} />
        <Route path="category/:cat" element={<CategoryPage />} />
        <Route path="product/:pid" element={<ProductPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="confirmed/:orderNo" element={<ConfirmedPage />} />
        <Route path="track" element={<TrackPage />} />
        <Route path="about" element={<ContentPage pageKey="about" fallbackTitle="About Us" />} />
        <Route path="contact" element={<ContentPage pageKey="contact" fallbackTitle="Contact Us" />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="shipping" element={<ContentPage pageKey="shipping" fallbackTitle="Shipping & Delivery" />} />
        <Route path="returns" element={<ContentPage pageKey="returns" fallbackTitle="Returns & Refunds" />} />
        <Route path="refunds" element={<ContentPage pageKey="refunds" fallbackTitle="Refund Policy" />} />
        <Route path="privacy" element={<ContentPage pageKey="privacy" fallbackTitle="Privacy Policy" />} />
        <Route path="terms" element={<ContentPage pageKey="terms" fallbackTitle="Terms & Conditions" />} />
        <Route path="cancellation" element={<ContentPage pageKey="cancellation" fallbackTitle="Cancellation Policy" />} />
        <Route path="payment-info" element={<ContentPage pageKey="payment-info" fallbackTitle="Payment Information" />} />
        <Route path="support" element={<ContentPage pageKey="support" fallbackTitle="Help & Support" />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:postSlug" element={<BlogPostPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="wishlist" element={<WishlistPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </StorefrontProvider>
  );
}
